import React, { useState, useEffect } from 'react'
import { injectIntl, FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { CreateUrl, CallGetAPI, CallPutAPI, CallPostAPI } from 'Utils/ApiHelper.js';
import moment from 'moment'
import BatteryGauge from 'react-battery-gauge'
import { BatteryInfoModel } from '../../models/APIGetBatteryInfoModel'
import Loader from '../../Utils/Loader/NewLoader2025'
import PieChart, {
    Series,
    Label,
    Export,
    Legend
} from 'devextreme-react/pie-chart';
import {
    Chart, Series as ChartSeries, CommonSeriesSettings, Legend as ChartLegend, Tooltip as ChartTooltip
} from 'devextreme-react/chart';
import { Button } from "@mui/material"
import ExcelJS, { Column } from 'exceljs';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';


interface Props {
    loggerId: number
    serial: number
}


interface PieChartData {
    type: string
    value: number
}

interface BatteryPercentageData {
    powerSavingConns: number
    remainingBatteryPerc: number
    remainingBatteryYears: string
}



interface PowerChartData {
    power: string
    unused: number
    nbioTClass0El1: number
    nbioTClass0El2: number
    nbioTClass0El3: number
    nbioTClass0El4: number
    nbioTClass0El5: number
    nbioTClass1El1: number
    nbioTClass1El2: number
    nbioTClass1El3: number
    nbioTClass1El4: number
    nbioTClass1El5: number
    nbioTClass2El1: number
    nbioTClass2El2: number
    nbioTClass2El3: number
    nbioTClass2El4: number
    nbioTClass2El5: number
    ltemClass0El1: number
    ltemClass0El2: number
    ltemClass0El3: number
    ltemClass0El4: number
    ltemClass0El5: number,
    ltemClass1El1: number
    ltemClass1El2: number
    ltemClass1El3: number
    ltemClass1El4: number
    ltemClass1El5: number
    reRequests: number
    failedConnections: number,
}

interface ExportHeaders {
    header: string
    key: string
}

const BatteryInfo: React.FC<Props> = ({ loggerId, serial }) => {

    //hooks
    const intl: IntlShape = useIntl();


    ///state
    const [loading, setLoading] = useState<boolean>(false)
    const [batteryInfoPieData, setBatteryInfoPieData] = useState<PieChartData[]>()
    const [batteryInfoData, setBatteryInfoData] = useState<BatteryInfoModel>()
    const [batteryAge, setBatteryAge] = useState<string>("")
    const [batteryPercentageData, setBatteryPercentageData] = useState<BatteryPercentageData>()
    const [start, setStart] = useState<Date>(moment().subtract(7, 'days').toDate())
    const [end, setEnd] = useState<Date>(moment().toDate())
    const [powerChartData, setPowerChartData] = useState<PowerChartData[]>()
    useEffect(() => {
        reloadData()
    }, [])

    const powerUse = {
        ltemClass0El1: 679,
        ltemClass0El2: 276,
        ltemClass0El3: 241,
        ltemClass0El4: 217,
        ltemClass0El5: 194,
        ltemClass1El1: 2800,
        ltemClass1El2: 2100,
        ltemClass1El3: 1800,
        ltemClass1El4: 1100,
        ltemClass1El5: 650,
        nbioTClass0El1: 650,
        nbioTClass0El2: 350,
        nbioTClass0El3: 240,
        nbioTClass0El4: 150,
        nbioTClass0El5: 140,
        nbioTClass1El1: 1800,
        nbioTClass1El2: 1090,
        nbioTClass1El3: 925,
        nbioTClass1El4: 589,
        nbioTClass1El5: 390,
        nbioTClass2El1: 14550,
        nbioTClass2El2: 71070, ///ask about this
        nbioTClass2El3: 3195,
        nbioTClass2El4: 2500,
        nbioTClass2El5: 1800,
        failedConnections: 12000,
        reRequest: 150
    }

    const energyLevelColors = {
        nbioTClass0El1: '#42A5F5',
        nbioTClass0El2: '#1E88E5',
        nbioTClass0El3: '#1565C0',
        nbioTClass0El4: '#0D47A1',
        nbioTClass0El5: '#01579B',
        nbioTClass1El1: '#BA68C8',
        nbioTClass1El2: '#9C27B0',
        nbioTClass1El3: '#7B1FA2',
        nbioTClass1El4: '#6A1B9A',
        nbioTClass1El5: '#4A148C',
        nbioTClass2El1: '#FFA726',
        nbioTClass2El2: '#FB8C00',
        nbioTClass2El3: '#EF6C00',
        nbioTClass2El4: '#E65100',
        nbioTClass2El5: '#BF360C',
        ltemClass0El1: '#66BB6A',
        ltemClass0El2: '#43A047',
        ltemClass0El3: '#2E7D32',
        ltemClass0El4: '#1B5E20',
        ltemClass0El5: '#0D4A12',
        ltemClass1El1: '#EC407A',
        ltemClass1El2: '#D81B60',
        ltemClass1El3: '#C2185B',
        ltemClass1El4: '#AD1457',
        ltemClass1El5: '#880E4F',
        reRequests: '#616161',
        failedConnections: '#C62828',
        unused: '#BDBDBD',
    };

    const totalBattery = 68400000

    const reloadData = () => {
        setLoading(true)
        CallGetAPI(CreateUrl(`/api/aquaguard/batteryinfo?loggerId=${loggerId}`), {})
            .then((data: BatteryInfoModel) => {
                const formattedPieChartOutput: PieChartData[] = []
                
                let totalUsedPower = 0
                const chartInstance = [{
                        power: 'Power',
                        unused: 0,
                        failedConnections: 0,
                        reRequests: 0,
                        nbioTClass0El1: 0,
                        nbioTClass0El2: 0,
                        nbioTClass0El3: 0,
                        nbioTClass0El4: 0,
                        nbioTClass0El5: 0,

                        nbioTClass1El1: 0,
                        nbioTClass1El2: 0,
                        nbioTClass1El3: 0,
                        nbioTClass1El4: 0,
                        nbioTClass1El5: 0,

                        nbioTClass2El1: 0,
                        nbioTClass2El2: 0,
                        nbioTClass2El3: 0,
                        nbioTClass2El4: 0,
                        nbioTClass2El5: 0,

                        ltemClass0El1: 0,
                        ltemClass0El2: 0,
                        ltemClass0El3: 0,
                        ltemClass0El4: 0,
                        ltemClass0El5: 0,

                        ltemClass1El1: 0,
                        ltemClass1El2: 0,
                        ltemClass1El3: 0,
                        ltemClass1El4: 0,
                        ltemClass1El5: 0,
                    }]
                
                Object.keys(data).forEach((key:string) => {
                    if (key.includes('nbioT') || key.includes('lte') || key.includes('failedConnections') || key.includes('reRequests')) {
                        if (key.includes('Conns')) {
                            ///power usage Data
                            if (key.includes('nbioT')) {
                                const classExtract = key.slice(10, 11);
                                const energyLevel = key.slice(13, 14);
                                if (classExtract === '0') {
                                    if (energyLevel === '1') {
                                        const power = data[key] * powerUse.nbioTClass0El1
                                        chartInstance[0].nbioTClass0El1 = power
                                        totalUsedPower += power
                                    }
                                    if (energyLevel === '2') {
                                        const power = data[key] * powerUse.nbioTClass0El2
                                        chartInstance[0].nbioTClass0El2 = power
                                        totalUsedPower += power
                                    }
                                    if (energyLevel === '3') {
                                        const power = data[key] * powerUse.nbioTClass0El3
                                        chartInstance[0].nbioTClass0El3 = power
                                        totalUsedPower += power
                                    }
                                    if (energyLevel === '4') {
                                        const power = data[key] * powerUse.nbioTClass0El4
                                        chartInstance[0].nbioTClass0El4 = power
                                        totalUsedPower += power
                                    }
                                    if (energyLevel === '5') {
                                        const power = data[key] * powerUse.nbioTClass0El5
                                        chartInstance[0].nbioTClass0El5 = power
                                        totalUsedPower += power
                                    }
                                }
                                if (classExtract === '1') {
                                    if (energyLevel === '1') {
                                        const power = data[key] * powerUse.nbioTClass1El1
                                        chartInstance[0].nbioTClass1El1 = power
                                        totalUsedPower += power
                                    }
                                    if (energyLevel === '2') {
                                        const power = data[key] * powerUse.nbioTClass1El2
                                        chartInstance[0].nbioTClass1El2 = power
                                        totalUsedPower += power
                                    }
                                    if (energyLevel === '3') {
                                        const power = data[key] * powerUse.nbioTClass1El3
                                        chartInstance[0].nbioTClass1El3 = power
                                        totalUsedPower += power
                                    }
                                    if (energyLevel === '4') {
                                        const power = data[key] * powerUse.nbioTClass1El4
                                        chartInstance[0].nbioTClass1El4 = power
                                        totalUsedPower += power
                                    }
                                    if (energyLevel === '5') {
                                        const power = data[key] * powerUse.nbioTClass1El5
                                        chartInstance[0].nbioTClass1El5 = power
                                        totalUsedPower += power
                                    }
                                }
                                if (classExtract === '2') {
                                    if (energyLevel === '1') {
                                        const power = data[key] * powerUse.nbioTClass2El1
                                        chartInstance[0].nbioTClass2El1 = power
                                        totalUsedPower += power
                                    }
                                    if (energyLevel === '2') {
                                        const power = data[key] * powerUse.nbioTClass2El2
                                        chartInstance[0].nbioTClass2El2 = power
                                        totalUsedPower += power
                                    }
                                    if (energyLevel === '3') {
                                        const power = data[key] * powerUse.nbioTClass2El3
                                        chartInstance[0].nbioTClass2El3 = power
                                        totalUsedPower += power
                                    }
                                    if (energyLevel === '4') {
                                        const power = data[key] * powerUse.nbioTClass2El4
                                        chartInstance[0].nbioTClass2El4 = power
                                        totalUsedPower += power
                                    }
                                    if (energyLevel === '5') {
                                        const power = data[key] * powerUse.nbioTClass2El5
                                        chartInstance[0].nbioTClass2El5 = power
                                        totalUsedPower += power
                                    }
                                }

                            } 
                            if (key.includes('lte')) {
                                const classExtract = key.slice(9, 10);
                                const energyLevel = key.slice(12, 13);
                                if (classExtract === '0') {
                                    if (energyLevel === '1') {
                                        const power = data[key] * powerUse.ltemClass0El1
                                        chartInstance[0].ltemClass0El1 = power
                                        totalUsedPower += power
                                    }
                                    if (energyLevel === '2') {
                                        const power = data[key] * powerUse.ltemClass0El2
                                        chartInstance[0].ltemClass0El2 = power
                                        totalUsedPower += power
                                    }
                                    if (energyLevel === '3') {
                                        const power = data[key] * powerUse.ltemClass0El3
                                        chartInstance[0].ltemClass0El3 = power
                                        totalUsedPower += power
                                    }
                                    if (energyLevel === '4') {
                                        const power = data[key] * powerUse.ltemClass0El4
                                        chartInstance[0].ltemClass0El4 = power
                                        totalUsedPower += power
                                    }
                                    if (energyLevel === '5') {
                                        const power = data[key] * powerUse.ltemClass0El5
                                        chartInstance[0].ltemClass0El5 = power
                                        totalUsedPower += power
                                    }
                                }
                                if (classExtract === '1') {
                                    if (energyLevel === '1') {
                                        const power = data[key] * powerUse.ltemClass1El1
                                        chartInstance[0].ltemClass1El1 = power
                                        totalUsedPower += power
                                    }
                                    if (energyLevel === '2') {
                                        const power = data[key] * powerUse.ltemClass1El2
                                        chartInstance[0].ltemClass1El2 = power
                                        totalUsedPower += power
                                    }
                                    if (energyLevel === '3') {
                                        const power = data[key] * powerUse.ltemClass1El3
                                        chartInstance[0].ltemClass1El3 = power
                                        totalUsedPower += power
                                    }
                                    if (energyLevel === '4') {
                                        const power = data[key] * powerUse.ltemClass1El4
                                        chartInstance[0].ltemClass1El4 = power
                                        totalUsedPower += power
                                    }
                                    if (energyLevel === '5') {
                                        const power = data[key] * powerUse.ltemClass1El5
                                        chartInstance[0].ltemClass1El5 = power
                                        totalUsedPower += power
                                    }
                                }
                            }
                            if (key.includes('failed')) {
                                const power = data[key] * powerUse.failedConnections 
                                chartInstance[0].failedConnections = power
                                totalUsedPower += power
                            }
                            if (key.includes('reRequests')) {
                                const power = data[key] * powerUse.reRequest
                                chartInstance[0].reRequests = power
                                totalUsedPower += power
                            }



                        /// pieChart Data
                            const input = { type: key, value: data[key] }
                            formattedPieChartOutput.push(input)
                        }
                    }
                })
                chartInstance[0].unused = totalBattery - totalUsedPower
                setPowerChartData(chartInstance)
                ///battery age modification
                
                setBatteryAge(calculateBatteryAge(data.batteryAgeDays))
                setBatteryInfoData(data)
                setBatteryInfoPieData(formattedPieChartOutput)
                setLoading(false)
            })
            .catch(err => {
                setBatteryInfoPieData([])
                console.error(err)
                setLoading(false)
            })
        CallGetAPI(CreateUrl(`/api/aquaguard/LoggerEvents?serial=${serial}&startDate=${moment(start).format("YYYY-MM-DD HH:mm:ss")}&endDate=${moment(end).format("YYYY-MM-DD HH:mm:ss")}`), {})
            .then(response => {
                const batteryPercentageDataTemp = response.filter(d => d.recordCode === 52).map(d => { 
                    if (moment(start).isSameOrAfter(moment().subtract(14, 'days'))) {
                        d.remainingBattYears = 'Calculating...'
                    } else if (d.remainingBattYears > 15) {
                        d.remainingBattYears = '15+'
                    } else {
                        d.remainingBattYears = d.remainingBattYears.toFixed(2)
                    }
                    
                    const batteryData: BatteryPercentageData = {
                        powerSavingConns: d.powerSavingConnsPerc,
                        remainingBatteryPerc: d.remainingBattPerc,
                        remainingBatteryYears: d.remainingBattYears,
                    }
                    return batteryData  
                })[0]
                setBatteryPercentageData(batteryPercentageDataTemp)
            })
            .catch(err => {
                console.error(err)
                setLoading(false)
            })

    }

    const calculateBatteryAge = (days):string => {
        let batteryAgeTemp = ""
        const batteryDays = days % 365
        if (days >= 365) {
            const batteryYears = Math.floor(days / 365)
            batteryAgeTemp = `${batteryYears} ${batteryYears > 1 ? 'Years' : 'Year'} ${batteryDays} ${batteryDays > 1 ? 'days' : 'day'}`
        } else {
            batteryAgeTemp = `${batteryDays} ${batteryDays > 1 ? 'days' : 'day'}`
        }
        return batteryAgeTemp
    }

    const renderText = (data): string => {
        if (data) {
            if (data.percent === 0) {
                return ""
            }
            if (data.argument.includes('nbioT')) {
                const classExtract = data.argument.slice(10, 11);
                const energyLevel = data.argument.slice(13, 14);
                return `NbIoT Class ${classExtract} Energy Level ${energyLevel}: ${parseFloat(data.percent * 100).toFixed(2)}% (${data.value} Connections)`
            } else if (data.argument.includes('lte')) {
                const classExtract = data.argument.slice(9, 10);
                const energyLevel = data.argument.slice(12, 13);
                return `LTE Class ${classExtract} Energy Level ${energyLevel}: ${parseFloat(data.percent * 100).toFixed(2)}% (${data.value} Connections)`
            } else if (data.argument.includes('failed')) {
                return `Network Errors ${parseFloat(data.percent * 100).toFixed(2)}% (${data.value} Failures )`
            } else if (data.argument.includes('reRequests')) {
                return `Re-Request Connections ${parseFloat(data.percent * 100).toFixed(2)}% (${data.value} Connections)`
            }
        }
    }

    const overViewStyle = {
        backgroundColor: "#f7f7f7",
        borderRadius: "5px",
        padding: "10px 0 0 0",
        width: "90%",
        boxShadow: "2px 2px 10px #00000060",
        margin: "20px",
        display: "flex",
        justifyContent: "center"
    }

    const custom = {
        batteryBody: {
            strokeWidth: 2
        },
        batteryCap: {
            strokeWidth: 2
        },
        batteryMeter: {
            fill: '#00BF00',
            lowBatteryValue: 15,
            lowBatteryFill: 'red',
            outerGap: 1,
            noOfCells: 1,
            interCellsGap: 1
        },
        readingText: {
            darkContrastColor: 'black'
        }
    }

    const renderSeries = (data: any): React.ReactElement[] => {
        const series: React.ReactElement[] = []
        Object.keys(data).forEach((key:string) => {
            if (data[key] !== 0 && typeof (data[key]) === 'number') {
                series.push(
                    <ChartSeries key={key} valueField={key} name={key} color={energyLevelColors[key]}> 
                    </ChartSeries>
                )
            }
        })
        return series
    }

    const customizePoint = (point: any): {color: string | undefined} => {
        return { color: energyLevelColors[point.argument.slice(0, point.argument.length - 5)] };
    }

    const handleExport = (): void => {
        type ExportData = {
            lastDataPoint: string
            batteryAge: string
            batteryStartDate: string
            totalConnections: number
            powerSaving: string
            SIMErrors: number
            rerequestConns?: number
            remainingBattery: string
            estimatedYears: string
            [key: string]: number
        }
        const defaultHeaders: ExportHeaders[] = []
        defaultHeaders.push({ header: 'Last Battery Data Point', key: 'lastDataPoint' })
        defaultHeaders.push({ header: 'Battery Age', key: 'batteryAge' })
        defaultHeaders.push({ header: 'Battery Start Date', key: 'batteryStartDate' })
        defaultHeaders.push({ header: 'Total Connections', key: 'totalConnections' })
        defaultHeaders.push({ header: 'Connections in Power Saving Mode', key: 'powerSaving' })
        
        defaultHeaders.push({ header: 'Remaining Battery %', key: 'remainingBattery' })
        defaultHeaders.push({ header: 'Remaining Years', key: 'estimatedYears' })

        const headers: ExportHeaders[] = []
        headers.push(...defaultHeaders)
        const filteredExportData: ExportData[] = []
        const exportObject: ExportData = {
            lastDataPoint: "",
            batteryAge: "",
            batteryStartDate: "",
            powerSaving: "",
            SIMErrors: 0,
            rerequestConns: 0,
            remainingBattery: "",
            estimatedYears: ""
}
        if (batteryInfoPieData && batteryInfoPieData.length > 0) {
            batteryInfoPieData.forEach((key: { type: string, value: number }): void => {
                if (key.value !== 0) {
                    exportObject[key.type] = key.value
                    if (key.type.includes('nbioT')) {
                        const classExtract = key.type.slice(10, 11);
                        const energyLevel = key.type.slice(13, 14);
                        const name = `NbIoT Class ${classExtract} Energy Level ${energyLevel} Connections`
                        headers.push({ header: name, key: key.type })
                    }
                    if (key.type.includes('lte')) {
                        const classExtract = key.type.slice(9, 10);
                        const energyLevel = key.type.slice(12, 13);
                        const name = `LTE Class ${classExtract} Energy Level ${energyLevel} Connections`
                        headers.push({ header: name, key: key.type })
                    }
                    if (key.type.includes('failed')) {
                        headers.push({ header: 'Network Errors', key: 'failedConnectionsConns'})
                    }
                    if (key.type.includes('reRequest')) {
                        headers.push({ header: 'Re-requests Connnections', key: 'rerequestConns' })
                    }
                }
            })
            
        }
        if (batteryInfoData && batteryPercentageData) {
            exportObject.lastDataPoint = moment(batteryInfoData.timeStamp).format('DD MMM YYYY HH:mm:ss')
            exportObject.batteryAge = calculateBatteryAge(batteryInfoData.batteryAgeDays)
            exportObject.batteryStartDate = moment(batteryInfoData.dateBatteryStarted).format('DD MMM YYYY HH:mm:ss')
            exportObject.totalConnections = batteryInfoData.totalConnections
            exportObject.powerSaving = `${batteryPercentageData.powerSavingConns}%`
            exportObject.SIMErrors = batteryInfoData.failedConnectionsConns
            exportObject.rerequestConns = batteryInfoData.reRequestsConns
            exportObject.remainingBattery = `${batteryPercentageData.remainingBatteryPerc}%`
            exportObject.estimatedYears = batteryPercentageData.remainingBatteryYears === 'Calculating...' ? 'N/A' : `${ batteryPercentageData.remainingBatteryYears } Years`
        }

        if (powerChartData && powerChartData.length > 0) {
            Object.keys(powerChartData[0]).forEach((key: string) => {

                if (powerChartData[0][key] !== 0) {
                    if (key.includes('nbioT')) {
                        const classExtract = key.slice(10, 11);
                        const energyLevel = key.slice(13, 14);
                        const name = `NbIoT Class ${classExtract} Energy Level ${energyLevel} Battery Use %`
                        headers.push({ header: name, key: key })
                    }
                    if (key.includes('lte')) {
                        const classExtract = key.slice(9, 10);
                        const energyLevel = key.slice(12, 13);
                        const name = `LTE Class ${classExtract} Energy Level ${energyLevel} Battery Use %`
                        headers.push({ header: name, key: key })
                    }
                    if (key.includes('failed')) {
                        headers.push({ header: 'Network Errors Battery Use %', key: 'failedConnections' })
                    }
                    if (key.includes('reRequests')) {
                        headers.push({ header: 'Re-requests Battery Use %', key: 'reRequests' })
                    }
                    
                    exportObject[key] = Math.round(((powerChartData[0][key] / totalBattery) * 100) * 1000) / 1000
                }
            })
        }
        filteredExportData.push(exportObject)
        const workbook = new ExcelJS.Workbook()
        let worksheet: ExcelJS.Worksheet | null = null 

        worksheet = workbook.addWorksheet(intl.formatMessage({ id: "Battery Info" }), {
            views: [{ state: 'frozen', xSplit: 0, ySplit: 1 }]
        });

        
        worksheet.columns = headers;
        
        worksheet.addRows(filteredExportData);


        const zip = new JSZip();
        const promises = workbook.worksheets.map(sheet => {
            const options: ExcelJS.CsvWriteOptions = { sheetName: sheet.name, sheetId: sheet.id, dateUTC: true };
            return workbook.csv.writeBuffer(options)
                .then((buffer: Buffer) => zip.file(sheet.name + ".csv", buffer));
        });

        Promise.all(promises).then(() => {
            if (workbook.worksheets.length > 0) {
                zip.generateAsync({ type: "blob" }).then(content => {
                    saveAs(content, `Battery information serial ${serial} recorded ${moment(batteryInfoData?.timeStamp).format('DD MMM YYYY')}.zip`);
                });
            }
            
        });
    }

    const customizeTooltip = (data: any): {text: string }=> {

        let name = ''
        if (data.seriesName === 'unused') {
            name = 'Unused Power'
        } if (data.seriesName === 'failedConnections') {
            name = 'Network Errors'
        } if (data.seriesName.includes('reRequests')) {
            name = 'Re-request Connections'
        } if (data.seriesName.includes('nbioT')) { 
            name = `NbIoT Class ${data.seriesName.slice(10, 11)} Energy Level ${data.seriesName.slice(13, 14)}`
        } if (data.seriesName.includes('lte')) {
            name = `LTE Class ${data.seriesName.slice(9, 10)} Energy Level ${data.seriesName.slice(12, 13)}`
        }
        return {
            text: `${name}`,
        };
    }
   
    return (
        <div>
            {loading && <div style={{
                position: 'relative', left: 'Calc(50% - 100px)', top: 'Calc(50vh - 200px)'
            }}>
                <Loader color={undefined} />
            </div>}
            {!loading &&
                <div>
                    <div style={{display: 'flex', justifyContent: 'flex-end'} }>
                        <Button variant="contained" color="primary" onClick={(): void => handleExport()}>Export</Button>
                    </div>
                    <div style={{ display: 'flex', width: '100%', height: '500px' }}>
                        <div style={{ borderLeft: "10px solid #00BF00", ...overViewStyle, width: "22%" }}>
                            <div>
                                <h4 style={{textAlign: 'center', marginBottom: '20px'} }><b>Overview</b></h4>
                                <p><b>Last Battery Data Point:</b> {moment(batteryInfoData?.timeStamp).format('DD MMM YYYY HH:mm:ss')}</p>
                                <p><b>Battery Age:</b> {batteryAge}</p>
                                <p><b>Battery Start Date:</b> {moment(batteryInfoData?.dateBatteryStarted).format('DD MMM YYYY HH:mm:ss')}</p>
                                <p><b>Total Connections:</b> {batteryInfoData?.totalConnections}</p>
                                <p><b>Connections in Power Saving Mode:</b> {Math.floor(batteryPercentageData?.powerSavingConns)}%</p>
                                <p><b>Network Errors:</b> {batteryInfoData?.failedConnectionsConns}</p>
                                <p><b>Re-Request Connections:</b> {batteryInfoData?.reRequestsConns}</p>
                                <div style={{ display: 'flex' }}>
                                    <p><b>Remaining Battery:</b></p>
                                    <div style={{margin: '-7px 0 0 20px'} }>
                                        {batteryPercentageData?.remainingBatteryPerc && <BatteryGauge value={batteryPercentageData.remainingBatteryPerc < 5 ? 5 : batteryPercentageData.remainingBatteryPerc} orientation="horizontal" size={70} animated={true} customization={custom} />}
                                    </div>
                                </div>
                                <p><b>Estimated Years Remaining:</b> {batteryPercentageData?.remainingBatteryYears} {batteryPercentageData?.remainingBatteryYears === 'Calculating...' ? '' : 'Years'}</p>
                            </div>
                        </div>
                        <div style={{ borderLeft: "10px solid #6F2DA8", ...overViewStyle, width: "20%" }}>
                            <div>
                            <h4 style={{ textAlign: 'center', marginBottom: "25px" }}><b>Breakdown of Power Used</b></h4>
                                <Chart
                                    dataSource={powerChartData}
                                    height={375}
                                >
                                    
                                    <CommonSeriesSettings argumentField="power" type="fullstackedbar" />
                                    {powerChartData && powerChartData.map((data) => {
                                        return renderSeries(data)
                                    })}
                                    <ChartLegend verticalAlignment="top"
                                        horizontalAlignment="center"
                                        itemTextPosition="right"
                                    />
                                    <ChartTooltip
                                        enabled={true}
                                        customizeTooltip={(data): { text: string } => customizeTooltip(data)}
                                    />
                                </Chart>
                            </div>
                        </div>
                        <div style={{ borderLeft: "10px solid #00ADBA", ...overViewStyle, width: "58%" }}>
                            <div style={{width: '100%'} }>
                                <h4 style={{ textAlign: 'center', marginBottom: "-25px" }}><b>Breakdown of Connections</b></h4>
                                <PieChart
                                    dataSource={batteryInfoPieData}
                                    resolveLabelOverlapping={'shift'}
                                    customizePoint={customizePoint}
                                >
                                    <Series
                                        argumentField="type"
                                        valueField="value"
                                    >
                                        <Label visible={true} customizeText={(data):string => renderText(data)}  />
                                    </Series>
                                    <Legend visible={false} />
                                    </PieChart>
                            </div>
                        </div>
                    </div>
                </div>
            }
        </div>
    )
}
export default BatteryInfo